import { useFrame, type ThreeElements } from '@react-three/fiber';
import { useControls } from 'leva';
import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three/webgpu';
import { useTexture } from '@react-three/drei';
import { useParticleSystem } from '../utils/particle-system';
import { color, Fn, texture, uniform, uv, vec3, vec4 } from 'three/tsl';

const Leaves = () => {
  const object3dRef = useRef<THREE.Object3D>(null!);
  // const object3dRefTwo = useRef<THREE.Object3D>(null!);
  // const object3dRefThree = useRef<THREE.Object3D>(null!);

  const particlesRef = useRef<ThreeElements['spriteNodeMaterial']>(null!);
  // const particlesRefTwo = useRef<ThreeElements['spriteNodeMaterial']>(null!);
  // const particlesRefThree = useRef<ThreeElements['spriteNodeMaterial']>(null!);

  const [leafAlphaTexture] = useTexture(['/texture/leaf.jpg']);

  const controls = useControls(
    'particles',
    {
      count: {
        value: 50,
      },
      floorY: {
        value: -0.95,
        onChange: (v) => {
          uniforms.floorY.value = v;
          // p2.uniforms.floorY.value = v;
          // p3.uniforms.floorY.value = v;
        },
        transient: false,
      },
      floorDamping: {
        value: 1,
        min: 0.01,
        max: 1,
        onChange: (v) => {
          uniforms.floorDamping.value = v;
          // p2.uniforms.floorY.value = v;
          // p3.uniforms.floorY.value = v;
        },
        transient: false,
      },
      decayFrequency: {
        value: 0.2,
        onChange: (v) => {
          uniforms.decayFrequency.value = v;
          // p2.uniforms.decayFrequency.value = v;
          // p3.uniforms.decayFrequency.value = v;
        },
        transient: false,
      },
      velocityDamping: {
        value: 0.01,
        onChange: (v) => {
          uniforms.velocityDamping.value = v;
          // p2.uniforms.velocityDamping.value = v;
          // p3.uniforms.velocityDamping.value = v;
        },
        transient: false,
      },
      emitterRadius: {
        value: 1,
        onChange: (v) => {
          uniforms.emitterRadius.value = v;
          // p2.uniforms.emitterRadius.value = v;
          // p3.uniforms.emitterRadius.value = v;
        },
        transient: false,
      },
      emitterVelocityStrength: {
        value: 0.439,
        onChange: (v) => {
          uniforms.emitterVelocityStrength.value = v;
          // p2.uniforms.emitterVelocityStrength.value = v;
          // p3.uniforms.emitterVelocityStrength.value = v;
        },
        transient: false,
      },

      initialVelocityX: {
        value: -2.0,
        onChange: (v) => {
          uniforms.initialVelocity.value.x = v;
          // p2.uniforms.initialVelocity.value.x = v;
          // p3.uniforms.initialVelocity.value.x = v;
        },
        transient: false,
      },
      initialVelocityY: {
        value: -2,
        onChange: (v) => {
          uniforms.initialVelocity.value.y = v;
          // p2.uniforms.initialVelocity.value.y = v;
          // p3.uniforms.initialVelocity.value.y = v;
        },
        transient: false,
      },
      initialVelocityZ: {
        value: -2.0,
        onChange: (v) => {
          uniforms.initialVelocity.value.z = v;
          // p2.uniforms.initialVelocity.value.z = v;
          // p3.uniforms.initialVelocity.value.z = v;
        },
        transient: false,
      },
      initialRandomVelocity: {
        value: 0,
        onChange: (v) => {
          uniforms.initialRandomVelocity.value = v;
          // p2.uniforms.initialRandomVelocity.value = v;
          // p3.uniforms.initialRandomVelocity.value = v;
        },
        transient: false,
      },
      turbulenceStrength: {
        value: 0.1,
        min: 0,
        max: 0.1,
        onChange: (v) => {
          uniforms.turbulenceStrength.value = v;
          // p2.uniforms.turbulenceStrength.value = v;
          // p3.uniforms.turbulenceStrength.value = v;
        },
        transient: false,
      },
      turbulenceTimeFrequency: {
        value: 0.27,
        min: 0,
        max: 1,
        onChange: (v) => {
          uniforms.turbulenceTimeFrequency.value = v;
          // p2.uniforms.turbulenceTimeFrequency.value = v;
          // p3.uniforms.turbulenceTimeFrequency.value = v;
        },
        transient: false,
      },
      turbulencePositionFrequency: {
        value: 1.7,
        min: 0,
        max: 10,
        onChange: (v) => {
          uniforms.turbulencePositionFrequency.value = v;
          // p2.uniforms.turbulencePositionFrequency.value = v;
          // p3.uniforms.turbulencePositionFrequency.value = v;
        },
        transient: false,
      },
      gravityX: {
        value: -0.403,
        onChange: (v) => {
          uniforms.gravity.value.x = v;
          // p2.uniforms.gravity.value.x = v;
          // p3.uniforms.gravity.value.x = v;
        },
        transient: false,
      },
      gravityY: {
        value: -0.98,
        onChange: (v) => {
          uniforms.gravity.value.y = v;
          // p2.uniforms.gravity.value.y = v;
          // p3.uniforms.gravity.value.y = v;
        },
        transient: false,
      },
      gravityZ: {
        value: 0.679,
        onChange: (v) => {
          uniforms.gravity.value.z = v;
          // p2.uniforms.gravity.value.z = v;
          // p3.uniforms.gravity.value.z = v;
        },
        transient: false,
      },
      leaveColorOne: {
        value: '#357D44',
        onChange: (v) => {
          colorUniforms.leafColor.value = new THREE.Color(v);
        },
        transient: false,
      },
      leaveColorTwo: {
        value: '#539E43FF',
        onChange: (v) => {
          colorUniforms.leafColorTwo.value = new THREE.Color(v);
        },
        transient: false,
      },
      fadeIn: {
        value: 0.0,
        onChange: (v) => {
          uniforms.fadeIn.value = v;
          // p2.uniforms.fadeIn.value = v;
          // p3.uniforms.fadeIn.value = v;
        },
        transient: false,
      },
      fadeOut: {
        value: 0.0,
        onChange: (v) => {
          uniforms.fadeOut.value = v;
          // p2.uniforms.fadeOut.value = v;
          // p3.uniforms.fadeOut.value = v;
        },
        transient: false,
      },
      size: {
        value: 0.0002,
        min: 0.0001,
        max: 1,
        onChange: (v) => {
          uniforms.size.value = v;
          // p2.uniforms.size.value = v;
          // p3.uniforms.size.value = v;
        },
      },
      scaleRangeStart: {
        value: 0.1,
        min: 0,
        max: 0.5,
        onChange: (v) => {
          uniforms.scaleRangeStart.value = v;
          // p2.uniforms.scaleRangeStart.value = v;
          // p3.uniforms.scaleRangeStart.value = v;
        },
        transient: false,
      },
      scaleRangeEnd: {
        value: 0.6,
        min: 0.5,
        max: 1,
        onChange: (v) => {
          uniforms.scaleRangeEnd.value = v;
          // p2.uniforms.scaleRangeEnd.value = v;
          // p3.uniforms.scaleRangeEnd.value = v;
        },
        transient: false,
      },
      glowSpread: {
        value: 0.02,
        onChange: (v) => {
          uniforms.glowSpread.value = v;
          // p2.uniforms.glowSpread.value = v;
          // p3.uniforms.glowSpread.value = v;
        },
        transient: false,
      },
      solidRatio: {
        value: 0.05,
        onChange: (v) => {
          uniforms.solidRatio.value = v;
          // p2.uniforms.solidRatio.value = v;
          // p3.uniforms.solidRatio.value = v;
        },
        transient: false,
      },
      solidAlpha: {
        value: 10,
        onChange: (v) => {
          uniforms.solidAlpha.value = v;
          // p2.uniforms.solidAlpha.value = v;
          // p3.uniforms.solidAlpha.value = v;
        },
        transient: false,
      },
      opacity: {
        value: 1,
        onChange: (v) => {
          uniforms.opacity.value = v;
          // p2.uniforms.opacity.value = v;
          // p3.uniforms.opacity.value = v;
        },
        transient: false,
      },
      sparklingAlpha: {
        value: 0,
        onChange: (v) => {
          uniforms.sparklingAlpha.value = v;
          // p2.uniforms.sparklingAlpha.value = v;
          // p3.uniforms.sparklingAlpha.value = v;
        },
        transient: false,
      },
      sparklingFrequency: {
        value: 0,
        onChange: (v) => {
          uniforms.sparklingFrequency.value = v;
          // p2.uniforms.sparklingFrequency.value = v;
          // p3.uniforms.sparklingFrequency.value = v;
        },
        transient: false,
      },
      sparklingDuration: {
        value: 0,
        onChange: (v) => {
          uniforms.sparklingDuration.value = v;
          // p2.uniforms.sparklingDuration.value = v;
          // p3.uniforms.sparklingDuration.value = v;
        },
        transient: false,
      },
    },
    { collapsed: true }
  );

  const { shaderNodes, uniforms, update } = useParticleSystem({
    count: controls.count,
    emitterObjects: object3dRef,
  });

  // const p2 = useParticleSystem({
  //   count: controls.count,
  //   emitterObjects: object3dRefTwo,
  // });

  // const p3 = useParticleSystem({
  //   count: controls.count,
  //   emitterObjects: object3dRefThree,
  // });

  useFrame((_state, delta) => {
    update(delta);
    // p2.update(delta);
    // p3.update(delta);
  });

  useEffect(() => {
    if (particlesRef.current) {
      particlesRef.current.needsUpdate = true;
    }
    // if (particlesRefTwo.current) {
    //   particlesRefTwo.current.needsUpdate = true;
    // }
    // if (particlesRefThree.current) {
    //   particlesRefThree.current.needsUpdate = true;
    // }
  }, []);

  const { colorNode, colorUniforms } = useMemo(() => {
    //uniforms
    const leafColor = uniform(color('#357D44'));
    const leafColorTwo = uniform(color('#539E43'));

    const colorNode = Fn(() => {
      const leaveAlphaTexture = texture(leafAlphaTexture, uv());
      const rChannel = leaveAlphaTexture.r.greaterThan(0);

      return vec4(vec3(leafColor), rChannel);
    })();
    return {
      colorNode,
      colorUniforms: {
        leafColor,
        leafColorTwo,
      },
    };
  }, [leafAlphaTexture]);

  return (
    <>
      <sprite count={controls.count}>
        <spriteNodeMaterial
          ref={particlesRef}
          positionNode={shaderNodes.posNode}
          colorNode={colorNode}
          scaleNode={shaderNodes.scaleNode}
          side={THREE.DoubleSide}
        />
      </sprite>
      <object3D
        ref={object3dRef}
        position={[8.187731956759574, 10.65359362050585, -6.254948482832837]}
      />
      {/* <TransformControls object={object3dRef} mode='translate' /> */}

      {/* <sprite count={controls.count}>
        <spriteNodeMaterial
          ref={particlesRefTwo}
          positionNode={p2.shaderNodes.posNode}
          colorNode={colorNode}
          scaleNode={p2.shaderNodes.scaleNode}
          side={THREE.DoubleSide}
        />
      </sprite>

      <object3D
        ref={object3dRefTwo}
        position={[14.96279861159735, 4.1061868610131995, -9.659082874675434]}
      /> */}
      {/* <TransformControls object={object3dRefTwo} mode='translate'  /> */}

      {/* <sprite count={controls.count}>
        <spriteNodeMaterial
          ref={particlesRefThree}
          positionNode={p3.shaderNodes.posNode}
          colorNode={colorNode}
          scaleNode={p3.shaderNodes.scaleNode}
          side={THREE.DoubleSide}
        />
      </sprite>

      <object3D
        ref={object3dRefThree}
        position={[16.249167399443504, 5.147969647703675, 3.2684058215182015]}
      /> */}
      {/* <TransformControls object={object3dRefThree} mode='translate'  /> */}

      {/* <gridHelper
        position={[0, controls.floorY, 0]}
        args={[142, 71, 0x303030, 0x303030]}
      /> */}
    </>
  );
};

export default React.memo(Leaves);
